'use client'

import { Carousel } from '@fukku/carousel'
import { CarouselVariants } from '@fukku/carousel/types'
import { Breakpoints } from '@fukku/styles/breakpoints'
import { dataTestIds } from 'plp/constants/dataTestIds'
import type { BannerItem } from 'plp/types/Banner'
import { useMemo } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import type { ImagesSizes } from 'utils/image/types'
import { Option } from './Option/Option'
import styles from './Options.module.scss'

const TOO_MANY_ITEMS = 6

interface Props {
	readonly data: BannerItem[]
	readonly catalogId: string
	readonly collection?: string
}

const FEW_ITEMS_ASPECT_RATIO = 0.7 // 7 / 10
const MANY_ITEMS_ASPECT_RATIO = 0.583 // 7 / 12

const SM_ITEM_SCREEN_PERCENTAGE = 40 // %

const invertAspectRatio = (aspectRatio: number) => 1 / aspectRatio

export const initialImageSizes: ImagesSizes[] = [
	{
		maxWidth: `${Breakpoints.large}px`,
		size: `${SM_ITEM_SCREEN_PERCENTAGE * invertAspectRatio(FEW_ITEMS_ASPECT_RATIO)}vw`,
	},
]

export function Options({ data, catalogId, collection }: Props) {
	const { isLessThanLarge } = useResponsive()

	const showWithFewItemsRatio = data.length <= TOO_MANY_ITEMS || isLessThanLarge

	const imageSizes = useMemo(() => {
		const screenPercentagePerImage = (1 / data.length) * 100
		return initialImageSizes.concat(
			[
				{
					size: `${screenPercentagePerImage * invertAspectRatio(MANY_ITEMS_ASPECT_RATIO)}vw`,
				},
			],
			[]
		)
	}, [data.length])

	const items = data.map((item, indexItem) => {
		if (item.image) {
			const hasCollection = item.collection ? `:${collection ?? ''}` : ''
			return (
				<Option
					key={`${item.catalogId}${item.collection ?? ''}`}
					item={item}
					isActive={
						item.catalogId === `${catalogId}${hasCollection}` ||
						(!collection && indexItem === 0)
					}
					showWithFewItemsRatio={showWithFewItemsRatio}
					indexItem={indexItem}
					imageSizes={imageSizes}
				/>
			)
		}
		return null
	})

	return (
		<div
			className={styles.container}
			data-testid={dataTestIds.plpQuickFiltersOptions('squared')}
		>
			{isLessThanLarge ? (
				<Carousel
					className={styles.carousel}
					dataTestId={dataTestIds.plpQuickFiltersCarousel('squared')}
					variant={CarouselVariants.VARIANT4}
				>
					{items}
				</Carousel>
			) : (
				items
			)}
		</div>
	)
}
